/* eslint-disable react-hooks/rules-of-hooks */
import React, {memo, Suspense} from "react"
import {
    useFavouritesGetCallbackObservable,
    useFavouritesAddCallbackObservable,
    useFavouritesRemoveCallbackObservable,
} from "@next/eventservice"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest/header"
import {Box} from "@mui/material"
import {HEADER_NAV_BAR_QUICK_LINKS, HEADER_NAV_BAR_QUICK_LINKS_FAVOURITES, NO_FOLLOW} from "../../config/constants"
import {Wrapper, StyledBox, StyledA} from "./components"
import connect from "./connect"
import {removeDeptFromSessionStorage} from "../../utils/removeDeptFromSessionStorage"

export type FavouritesProps = {
    altText: string
    enableFavourites: boolean
    updateFavourites: (data: any) => void
    favouritesUrl: string
    anchorStyles?: React.ComponentPropsWithoutRef<typeof Box>["sx"]
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    hasFavourites: boolean
    realm: string
    variant: string
    iconsVersionPath: string
}

export const Favourites = memo(
    ({
        altText,
        enableFavourites,
        updateFavourites,
        favouritesUrl,
        anchorStyles,
        templating,
        hasFavourites,
        realm,
        variant,
        iconsVersionPath,
    }: FavouritesProps) => {
        if (!enableFavourites) return null
        const ComponentName = useTemplatedComponent(templating, MANIFEST)
        const transUpdateFavourites = (data: any) => requestIdleCallback(() => updateFavourites(data))
        useFavouritesGetCallbackObservable(data => transUpdateFavourites(data))
        useFavouritesAddCallbackObservable(data => transUpdateFavourites(data))
        useFavouritesRemoveCallbackObservable(data => transUpdateFavourites(data))

        // Todo: User Story 385524 - Migrate all clients to use Favourites Badge to suit UX designs/requirements
        // https://dev.azure.com/Next-Technology/Ecom.Backlog/_workitems/edit/385524
        return (
            <Wrapper onClick={removeDeptFromSessionStorage} className="favourites" data-testid="header-favourites">
                <StyledBox sx={{...anchorStyles}}>
                    <StyledA
                        href={favouritesUrl}
                        data-ga-v1={HEADER_NAV_BAR_QUICK_LINKS}
                        data-ga-v2={HEADER_NAV_BAR_QUICK_LINKS_FAVOURITES}
                        rel={NO_FOLLOW}
                        aria-label={HEADER_NAV_BAR_QUICK_LINKS_FAVOURITES}
                        title={HEADER_NAV_BAR_QUICK_LINKS_FAVOURITES}
                    >
                        <Suspense>
                            <ComponentName
                                realm={realm}
                                variant={variant}
                                iconsVersionPath={iconsVersionPath}
                                altText={altText}
                                hasFavourites={hasFavourites}
                            />
                        </Suspense>
                    </StyledA>
                </StyledBox>
            </Wrapper>
        )
    },
)
Favourites.displayName = "Favourites"

export default connect(Favourites)
